import React, { useCallback } from 'react';
import cn from 'classnames';
import { useAuthContext } from '@hooks/use-auth-context';
import { Key, KeyWithTrigger } from '@components/KeyboardShortcut';
import * as DM from '@radix-ui/react-dropdown-menu';
import DropdownMenu from '@components/Menus/DropdownMenu';
import { ArrowRightIcon, CogIcon, GiftIcon, ShortcutsIcon } from '@components/Icons';
import alertIconMap from '@components/Icons/AlertIcons';
import styles from './UdoMenu.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { selectUdoMenuSystemAlert } from '@actions/alerts';
import { appVersion } from '@app/config';
import { buildReferralActions } from '@support/referralSharing';
import analytics from '@support/analytics';
import { navigateToSettings, openModal } from '@actions/ui';
import { ProBadge, TeamsBadge } from '@components/Icons/ProductNameBadge';
import { DropdownMenuOption } from '@app/types';
import { selectNavBarState } from '@reducers/ui/navBar';
import upgradeAction from '@support/upgrade-action';

function AppBuildVersion() {
  const { getIsProPlan, getIsTeamsPlan } = useAuthContext();

  let version = appVersion;
  if (window.app.isElectron && window.desktopIntegrations) {
    version = `${version} (${window.desktopIntegrations.appInfo.version})`;
  }

  return (
    <DM.Label className={cn(styles.header, 'text-small')}>
      <div>Clay</div>
      {getIsProPlan() && <ProBadge />}
      {getIsTeamsPlan() && <TeamsBadge />}
      <div className={styles.build}>{version}</div>
    </DM.Label>
  );
}

export default function UdoMenu({ className }: { className?: string }) {
  const dispatch = useDispatch();
  const { user, getIsFreePlan } = useAuthContext();
  const alert = useSelector(selectUdoMenuSystemAlert);
  const { status } = useSelector(selectNavBarState);

  const handleHelpCenter = useCallback(() => {
    window.open('https://library.clay.earth/', '_blank');
  }, []);

  const handleSupport = useCallback(() => {
    analytics.track('Feedback Tapped');
    dispatch(openModal('support'));
  }, []);

  const handleKeyboardShortcuts = useCallback(() => {
    window.app.emit('shortcuts:open');
  }, []);

  let menuOptions: DropdownMenuOption[] = [
    {
      title: 'Settings',
      icon: CogIcon,
      action: () => dispatch(navigateToSettings()),
      suffix: <KeyWithTrigger keyChar="," />,
    },
    {
      title: 'Keyboard Shortcuts',
      action: handleKeyboardShortcuts,
      icon: ShortcutsIcon,
      suffix: <Key>?</Key>,
    },
    {
      title: 'Invite a Friend',
      icon: GiftIcon,
      submenu: buildReferralActions(user, dispatch),
    },
    { divider: true, disabled: true },
    { title: 'Help Center', action: handleHelpCenter },
    { title: 'Support', action: handleSupport },
  ];

  if (getIsFreePlan()) {
    menuOptions = [
      {
        button: (
          <span>
            <span>
              Upgrade Account
              <ArrowRightIcon color="textPrimary" />
            </span>
            <ProBadge />
          </span>
        ),
        action: upgradeAction(user, dispatch),
        className: styles.upgradeBtn,
        focusedClassName: styles.upgradeBtnFocused,
      },
      ...menuOptions,
    ];
  }

  if (alert) {
    menuOptions = [
      ...menuOptions,
      {
        title: alert.label,
        action: () => window.open(alert.url, '_blank'),
        icon: alertIconMap[alert.severity],
        className: cn(styles.alert, styles[`alert_${alert.severity}`]),
      },
    ];
  }

  return !!user ? (
    <div
      className={cn(styles.wrapper, className, styles[`navBar-${status}`])}
      key={alert?.id || '0'}
    >
      <DropdownMenu
        triggerVariant="udo"
        menuClassName={styles.dropdownMenuContainer}
        before={<AppBuildVersion />}
        options={menuOptions}
      />
    </div>
  ) : null;
}
